 
import { I18n } from '@/i18n/I18n';
import { Operation } from '@/backend/models/operation';
import { useEffect, useState } from 'react';
import { ParameterSubjectType, ParameterType } from '@/types/parameterTypes';
import { useShallow } from 'zustand/react/shallow';
import { Code } from '@/backend/models/code';
import { OverlayTrigger, Placeholder, Tooltip } from 'react-bootstrap';
import { ParameterRun } from '@/frontend/components/parameters/ParameterRun';
import { useParameterStore } from '@/frontend/store/ParameterStore';
import { LoadingSpinner } from '../LoadingSpinner';
import { Play } from '@/frontend/SVGs/Play';
import { MonacoPythonEditor } from '../MonacoPythonEditor';
import { CodeOutput } from './CodeOutput';
import { nativeBridge } from '@/frontend/embed/nativeBridge';
import { useCodeRunStore } from '@/frontend/store/CodeRunStore';
import { CodeStatus } from '@/types/codeTypes';
import { ExclamationTriangle } from '@/frontend/SVGs/ExclaimationTriangle';

export function CodeItemRun(props: { operation: Operation }) {
  const { operation } = props;
  const inputParams = useParameterStore(useShallow((state) => state.parametersForSubjectAndType(operation.id, ParameterType.INPUT)));

  const code = operation.code as Code;

  useEffect(() => {
    const staleInputParams = inputParams.filter(param => param.fields.some(field => {
      const updatedAt = field.getUpdatedAt();
      return code.lastRanAt && updatedAt && updatedAt > code.lastRanAt;
    }));
    if (staleInputParams.length > 0 && code.returnCode !== CodeStatus.RUN_PENDING) {
      setStaleError(true);
    } else {
      setStaleError(false);
    }
  }, [inputParams, operation.updatedAt]);

  const [showCode, setShowCode] = useState(false);
  const [staleError, setStaleError] = useState(false);

  // The runner is a process on the user's own machine, so there is nothing to
  // run against in an ordinary browser: the button says so rather than failing
  // when it is pressed.
  const available = nativeBridge.isAvailable();
  const runCode = useCodeRunStore(state => state.runCode);
  const running = useCodeRunStore(state => Boolean(state.runningByOperationId[operation.id]));
  const runError = useCodeRunStore(state => state.runErrorByOperationId[operation.id]);

  const label = `${I18n.get('runItem', { itemType: I18n.get('operation') })} ${operation.title}`;

  return (
    <div>
      {runError && (
        <div className='m-2 d-flex gap-2 align-items-center d-print-none'>
          <ExclamationTriangle color="red" />
          <small className='danger-subtle'>{runError}</small>
        </div>
      )}
      {staleError && (
        <div className='m-2 d-flex gap-2 align-items-center d-print-none'>
          <ExclamationTriangle color="red" />
          <small className='danger-subtle'>{I18n.get('staleCodeWarning')}</small>
        </ div>
      )}
      <div className={`mb-3 ${staleError ? 'card border border-danger' : ''}`}>
        <ParameterRun id={operation.id} parameterType={ParameterType.INPUT } parentType={ParameterSubjectType.OPERATION} />
      </div>

      <div className='d-flex justify-content-between'>
        <OverlayTrigger
          placement="top"
          delay={{ show: 250, hide: 400 }}
          overlay={<Tooltip>{I18n.get(available ? 'executeCodeTooltip' : 'codeRunNotAvailable')}</Tooltip>}
        >
          <div style={{ maxWidth: 'fit-content' }}><button
            aria-label={label}
            disabled={!available || running}
            onClick={() => void runCode(operation)}
            type='button'
            className='btn d-block btn-success'>
              <div className='d-flex align-items-center gap-2'>
                {running || code.returnCode === CodeStatus.RUN_PENDING ? <LoadingSpinner /> : <Play />}
                {I18n.get('executeCode')}
              </div>
          </button></div>
        </OverlayTrigger>
        <button
          onClick={() => setShowCode(!showCode)}
          className='btn btn-secondary'
          type='button'>{!showCode ? I18n.get('showAdvancedView') : I18n.get('hideAdvancedView')}
        </button>
      </div>

      {showCode && (
        <div className='mt-3'>
          <MonacoPythonEditor script={code.script} readOnly />
        </div>
      )}
      {code.lastRanAt && showCode && (
        <>
          <p className='mt-3'>{I18n.get('output')}</p>
          {code.stdout && (<code style={{whiteSpace: 'pre-wrap'}} className='d-block mb-3'>{I18n.get('stdout', {stdout: code.stdout})}</code>)}
          {code.stderr && (<code style={{whiteSpace: 'pre-wrap'}} className='d-block mb-3'>{I18n.get('stderr', {stderr: code.stderr})}</code>)}
          {code.returnCode !== CodeStatus.RUN_PENDING && (<code className='d-block'>{I18n.get('returnCode', {returnCode: code.returnCode.toString()})}</code>)}
          {code.returnCode === CodeStatus.RUN_PENDING && (
            <Placeholder animation='wave'>
              <Placeholder.Button className='btn btn-secondary' size='sm' xs={3} />
            </Placeholder>
          )}
          <div className='mt-3'>
            <CodeOutput operationId={operation.id} />
          </div>
        </>
      )}
    </div>
  );
}
